import React, { useState } from 'react';
import JobApplyForm from '@fuww/library/src/JobApplyForm';
import { ApolloError, useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { SubmitHandler } from 'react-hook-form';
import withQuery from '../../../lib/withQuery';
import UploadProgress from './UploadProgress';
import messages from '../../../lib/messages.mjs';
import createApplicantMutation
  from '../../../lib/mutations/createApplicant.graphql';
import { useSiteContext } from '../../SiteContext';
import currentUserQuery
  from '../../../lib/queries/users/currentUserQuery.graphql';
import getEmailSuggestionFormatter
  from '../../../lib/getEmailSuggestionFormatter';
import {
  currentUserImageConfigurations,
} from '../../../lib/imageConfigurations';

const ApplyForm = ({
  jobId, setUploadSuccess, data: { currentUser },
}: {
  jobId: number;
  setUploadSuccess: (success: boolean) => void;
  data: {
    currentUser: {
      firstName?: string;
      lastName?: string;
      username?: string;
    };
  };
}) => {
  const intl = useIntl();
  const { jobsLocale } = useSiteContext();

  const { firstName, lastName, username: email } = { ...currentUser };

  const [uploading, setUploading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [graphqlError, setGraphqlError] = useState<ApolloError | null>(null);

  const [
    createApplicant,
  ] = useMutation(createApplicantMutation, {
    onError: (error) => setGraphqlError(error),
    onCompleted: () => setUploadSuccess(true),
  });

  const submitHandler: SubmitHandler<{
    jobId: number;
    locale: string;
    firstName: string;
    lastName: string;
    email: string;
    subscribedToNewsletter: boolean;
    motivation?: string;
    coverLetter: File | null;
    cv: File;
    extraDocument1: File | null;
    extraDocument2: File | null;
  }> = (data) => {
    setUploading(true);
    setSubmitDisabled(true);
    setGraphqlError(null);

    const files = {
      coverLetter: data.coverLetter,
      cv: data.cv,
      extraDocument1: data.extraDocument1,
      extraDocument2: data.extraDocument2,
    };

    createApplicant({
      variables: {
        jobId,
        locale: jobsLocale,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        subscribedToNewsletter: data.subscribedToNewsletter,
        motivation: data.motivation,
        ...files,
      },
    });
  };

  return (
    <JobApplyForm
      submitHandler={submitHandler}
      defaultValues={{
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        email: email ?? '',
        cv: null,
        motivation: '',
        coverLetter: null,
        extraDocument1: null,
        extraDocument2: null,
        subscribedToNewsletter: false,
      }}
      submitDisabled={submitDisabled}
      graphqlError={graphqlError}
      firstNameLabel={intl.formatMessage(messages.firstName)}
      lastNameLabel={intl.formatMessage(messages.lastName)}
      emailAddressLabel={intl.formatMessage(messages.emailAddress)}
      cvLabel={intl.formatMessage(messages['applicantForm.cvLabel'])}
      motivationLabel={intl.formatMessage(messages.motivation)}
      coverLetterLabel={
        intl.formatMessage(messages['applicantForm.coverLetterLabel'])
      }
      motivationHelpText={
        intl.formatMessage(messages['applicantForm.motivationHelpText'])
      }
      extraDocument1Label={
        intl.formatMessage(messages['applicantForm.extraDocument1Label'])
      }
      extraDocument2Label={
        intl.formatMessage(messages['applicantForm.extraDocument2Label'])
      }
      subscribedToNewsletterLabel={intl.formatMessage(
        messages['applicantForm.subscribedToNewsletterLabel'],
      )}
      submitLabel={intl.formatMessage(messages['applicantForm.button'])}
      noFileSelectedLabel={
        intl.formatMessage(messages['applicantForm.noFileSelected'])
      }
      allowedFileFormatsLabel={
        intl.formatMessage(messages['applicantForm.allowedFileFormats'])
      }
      requiredValidationMessage={
        intl.formatMessage(messages['applicantForm.fieldRequired'])
      }
      emailValidationMessage={
        intl.formatMessage(messages['applicantForm.mustBeValidEmailAddress'])
      }
      fileIsEmptyMessage={
        intl.formatMessage(messages['applicantForm.fileIsEmpty'])
      }
      fileMaxSizeExceededMessage={
        intl.formatMessage(messages['applicantForm.fileMaxSizeExceeded'])
      }
      fileHasInvalidTypeMessage={
        intl.formatMessage(messages['applicantForm.fileHasInvalidType'])
      }
      formatEmailSuggestion={getEmailSuggestionFormatter(intl)}
      uploadProgress={uploading ? <UploadProgress /> : null}
    />
  );
};

const ApplyFormWithQuery = withQuery(ApplyForm);

const ApplyFormWithData = ({ jobId, setUploadSuccess }: {
  jobId: number;
  setUploadSuccess: (success: boolean) => void;
}) => (
  <ApplyFormWithQuery
    query={currentUserQuery}
    variables={{
      imageConfigurations: currentUserImageConfigurations,
    }}
    errorMessage="Error loading current User."
    jobId={jobId}
    setUploadSuccess={setUploadSuccess}
  />
);

export default ApplyFormWithData;
